import { Box, Stack, Typography, Input } from "@mui/joy"
import Card from "@mui/joy/Card"
import CardContent from "@mui/joy/CardContent"
import Button from "@mui/joy/Button"
import { styled } from "@mui/joy/styles"
import { useState, useEffect, useMemo } from "react"
import { Link } from "react-router-dom"
import { useMediaQuery } from '@mui/material';
import { getProducts, getCategories } from '../services/api';
import { normalizeImagePath } from '../utils/imagePath';
import SearchIcon from '@mui/icons-material/Search';

const AdaptiveGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 300px))",
  gap: "30px",
  width: "100%",
  marginTop: 10,
  "@media (max-width: 768px)": {
    gridTemplateColumns: "1fr",
    gap: "20px",
  }
})

const StyledCard = styled(Card)({
  borderRadius: "20px",
  padding: 0,
  boxShadow: "0px 2px 4px rgba(255, 0, 0, 0.2)",
  overflow: "hidden",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  maxWidth: "300px",
  width: "100%",
  margin: "0 auto",
  textDecoration: "none",
  color: "inherit",
  "@media (max-width: 768px)": {
    maxWidth: "100%",
  }
})

function Catalog() {
  const [products, setProducts] = useState([]);
  const [categoryNames, setCategoryNames] = useState({});
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const [prodData, catData] = await Promise.all([getProducts(), getCategories()]);
        setProducts(Array.isArray(prodData) ? prodData : []);
        const map = {};
        (Array.isArray(catData) ? catData : []).forEach(c => { map[c.id] = c.name; });
        setCategoryNames(map);
      } catch (e) {
        console.error('Failed to load catalog data', e);
      }
      setLoadingCatalog(false);
    };
    fetchCatalog();
  }, []);

  const [cart, setCart] = useState([])
  const isMobile = useMediaQuery('(max-width: 768px)')

  const priceOnRequestProductIds = new Set([5013, 5014, 5015])

  const getProductPriceLabel = (product) => {
    if (!product) return ""
    if (priceOnRequestProductIds.has(product.id)) return "Цена по запросу"
    return `${product.price} руб.`
  }

  useEffect(() => {
    document.title = 'NASCAR — Каталог';
  }, []);

  useEffect(() => {
    const loadCart = () => {
      try {
        const savedCart = localStorage.getItem("cart")
        if (savedCart) {
          const parsedCart = JSON.parse(savedCart)
          const validCart = parsedCart.filter((item) => item.quantity > 0)
          setCart(validCart)
          if (validCart.length !== parsedCart.length) {
            localStorage.setItem("cart", JSON.stringify(validCart))
          }
        }
      } catch (error) {
        console.error("Error loading cart:", error)
        setCart([])
      }
    }

    loadCart()
    window.addEventListener("cartUpdated", loadCart)
    return () => window.removeEventListener("cartUpdated", loadCart)
  }, [])

  const addToCart = (product) => {
    const savedCart = localStorage.getItem("cart")
    let currentCart = []

    if (savedCart) {
      try {
        currentCart = JSON.parse(savedCart)
      } catch (error) {
        console.error("Error parsing cart:", error)
      }
    }

    const existingItemIndex = currentCart.findIndex((item) => item.id === product.id)

    if (existingItemIndex !== -1) {
      currentCart[existingItemIndex].quantity += 1
    } else {
      currentCart.push({ ...product, quantity: 1 })
    }

    localStorage.setItem("cart", JSON.stringify(currentCart))
    setCart(currentCart)
    window.dispatchEvent(new CustomEvent("cartUpdated", { detail: currentCart }))
  }

  const isInCart = (productId) => {
    return cart.some((item) => item.id === productId)
  }

  const handleCartButtonClick = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isInCart(product.id)) {
      window.dispatchEvent(new CustomEvent("openCart"))
    } else {
      addToCart(product)
    }
  }

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return products;
    return products.filter(p =>
      p.name.toLowerCase().includes(term) ||
      (p.description && p.description.toLowerCase().includes(term))
    );
  }, [products, search]);

  const productsByCategory = useMemo(() => {
    return filteredProducts.reduce((acc, product) => {
      if (!acc[product.category_id]) {
        acc[product.category_id] = [];
      }
      acc[product.category_id].push(product);
      return acc;
    }, {});
  }, [filteredProducts]);

  if (loadingCatalog) {
    return (
      <Box sx={{ width: "100%", px: 2.5, py: 2, minHeight: "600px", display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography level="h2">Загрузка каталога...</Typography>
      </Box>
    );
  }

  if (!Array.isArray(products)) {
    return (
      <Box sx={{ width: "100%", px: 2.5, py: 2, minHeight: "600px", display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography level="h2" sx={{ color: 'red' }}>Ошибка загрузки каталога. Проверьте сервер.</Typography>
      </Box>
    );
  }

  return (
    <Box id="catalog-container" sx={{ width: "100%", px: 2.5, py: 2, minHeight: "600px" }}>
      <Typography level="h1" sx={{ mb: 2 }}>Каталог</Typography>

      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Поиск по названию или описанию..."
        startDecorator={<SearchIcon />}
        sx={{ maxWidth: 500, mb: 3, borderRadius: 250 }}
      />

      {Object.keys(productsByCategory).length === 0 && (
        <Typography level="h3" sx={{ textAlign: 'center', mt: 6, color: 'neutral.500' }}>
          По вашему запросу ничего не найдено
        </Typography>
      )}

      {Object.entries(productsByCategory).map(([categoryId, categoryProducts]) => (
        <Box key={categoryId} sx={{ mt: 2 }}>
          <Typography level="h3" sx={{ mb: 2 }}>
            {categoryNames[categoryId]}
          </Typography>
          <AdaptiveGrid sx={{ justifyContent: isMobile ? 'center' : 'none' }}>
            {categoryProducts.map((product) => (
              <StyledCard
                key={product.id}
                variant="plain"
                component={Link}
                to={`/product/${product.id}`}
              >
                <img
                  src={normalizeImagePath(product.image) || "/placeholder.svg"}
                  alt={product.name}
                  style={{ width: "100%", height: "400px", objectFit: "cover" }}
                />
                <CardContent sx={{ p: 2, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                  <Stack sx={{ flexGrow: 1 }}>
                    <Typography noWrap level="h3">
                      {product.name}
                    </Typography>
                    <Typography noWrap level="body-sm" sx={{ mb: 1 }}>
                      {product.description}
                    </Typography>
                    <Typography level="title-sm">
                      {priceOnRequestProductIds.has(product.id)
                        ? getProductPriceLabel(product)
                        : `Цена: ${getProductPriceLabel(product)}`}
                    </Typography>
                  </Stack>
                  <Button
                    variant={isInCart(product.id) ? "soft" : "solid"}
                    color="danger"
                    sx={{
                      mt: 2,
                      py: 2,
                      width: "100%",
                      borderRadius: 250,
                    }}
                    disabled={product.quantity <= 0}
                    onClick={(e) => handleCartButtonClick(product, e)}
                  >
                    {product.quantity > 0 ? (isInCart(product.id) ? "В корзине" : "Добавить в корзину") : "Нет в наличии"}
                  </Button>
                </CardContent>
              </StyledCard>
            ))}
          </AdaptiveGrid>
        </Box>
      ))}
    </Box>
  )
}

export default Catalog
